import { useEffect, useRef } from "react";
import Subscription from "../../actions/Subscription";
import Controls from "../controls/Controls";
import { Form, useForm } from "../customHelpers/useForm";
import { service } from "../../utils/utils";
import { FormControlLabel, Grid, Switch, TextField, Typography } from "@mui/material";
import produce from "immer";

const numericServices = [
  "Number of SKUs",
  "Number of Users",
  "Number of Stores",
];

const checkboxServices = [
  "Activity Report",
  "Inventory Report",
  "Payment Report",
  "Sales Report",
  "Purchase Order Report"
];

const initialFValues = {
  name: "",
  description: "",
  services: [
    ...service().countables.map((name) => ({ name, id: null, value: "" })),
    ...service().reports.map((name) => ({ name, id: null, value: false })),
  ],
  submitting: false,
};

const AddSubscription = ({
  NotifyMessage,
  setOpenPopup,
  recordForEdit,
  getSubscription,
  edite,
}) => {
  const {
    addSubscription,
    updateSubscriptionService,
    updateSubscription,
    addService,
  } = Subscription();
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (recordForEdit != null) {
      const updatedServices = [
        ...service().countables.map((name) => {
          const service = recordForEdit.subscriptionServices.find(
            (s) => s.name === name
          );
          return {
            name,
            id: service ? service.id : null,
            value: service ? service.value : "",
          };
        }),
        ...service().reports.map((name) => {
          const service = recordForEdit.subscriptionServices.find(
            (s) => s.name === name
          );
          return {
            name,
            id: service ? service.id : null,
            value: service ? service.value === "on" : false,
          };
        }),
      ];

      setValues({
        id: recordForEdit.id,
        name: recordForEdit.name || "",
        description: recordForEdit.description || "",
        services: updatedServices,
        submitting: false,
      });
    }
  }, [recordForEdit]);

  useEffect(() => {
    if (firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, []);

  const validate = (fieldValues = values) => {
    const temp = { ...errors };
    if ("name" in fieldValues)
      temp.name =
        fieldValues.name.length >= 2
          ? ""
          : "Name must be at least 2 characters.";
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const { values, errors, setValues, handleInputChange, setErrors, resetForm } =
    useForm(initialFValues, true, validate);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      setValues({ ...values, submitting: true });

      const payload = {
        name: values.name,
        description: values.description,
      };

      const servicePayload = {
        services: values.services.map((s) => ({
          id: s.id,
          name: s.name,
          subscription_Id: values.id,
          value: checkboxServices.includes(s.name)
            ? s.value
              ? "on"
              : "off"
            : s.value,
        })),
      };

      if (edite === false) {
        addSubscription(payload, values.id).then((data) => {
          if (data.err) {
            setValues({ ...values, submitting: false });
            NotifyMessage({ message: data.err, type: "error" });
          } else if (data.subscription) {
            NotifyMessage({
              message: "Subscription successfully created",
              type: "success",
            });
            addService({
              subscription_Id: data.subscription.id,
              ...servicePayload,
            }).then((serviceData) => {
              if (serviceData.err) {
                NotifyMessage({
                  message: serviceData.err,
                  type: "error",
                });
              }
              getSubscription();
              setOpenPopup(false);
              resetForm();
            });
          }
        });
      } else {
        updateSubscription(payload, values.id).then((data) => {
          if (data.err) {
            setValues({ ...values, submitting: false });
            NotifyMessage({ message: data.err, type: "error" });
          } else {
            updateSubscriptionService(servicePayload).then((serviceData) => {
              if (serviceData.err) {
                setValues({ ...values, submitting: false });
                NotifyMessage({
                  message: serviceData.err,
                  type: "error",
                });
              } else {
                NotifyMessage({
                  message: "Subscription successfully updated",
                  type: "success",
                });
                getSubscription();
                setOpenPopup(false);
                resetForm();
              }
            });
          }
        });
      }
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            style={{
              fontWeight: 400,
              color: "rgba(244,151,3,.8)",
              margin: "0.1rem 0 0.1rem 0.1rem",
            }}
          >
            Description
          </Typography>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid #e0e0e0",
              margin: "0 1rem 0 1rem",
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controls.Input
            name="name"
            label="Name"
            disabled={
              recordForEdit?.name?.trim() === "Default" ||
              values.name?.trim() === "Default"
            }
            value={values.name}
            onChange={handleInputChange}
            error={errors.name}
            inputRef={firstFieldRef}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controls.Input
            name="description"
            label="Description"
            value={values.description}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            style={{
              fontWeight: 400,
              color: "rgba(244,151,3,.8)",
              margin: "0.1rem 0 0.1rem 0.1rem",
            }}
          >
            Basic
          </Typography>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid #e0e0e0",
              margin: "0 1rem 0 1rem",
            }}
          />
        </Grid>
        {values.services
          .filter((item) => numericServices.includes(item.name))
          .map((item) => {
            const serviceIndex = values.services.findIndex(
              (s) => s.name === item.name
            );
            return (
              <Grid item xs={12} sm={4} key={item.name}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.2rem",
                    marginLeft: "1rem",
                  }}
                >
                  <Typography variant="body1" style={{ minWidth: "150px" }}>
                    <strong>{item.name}</strong>
                  </Typography>
                  <TextField
                    size="small"
                    name={`services[${serviceIndex}].value`}
                    type="number"
                    label="Value"
                    value={item.value}
                    onChange={(e) => {
                      const updated = [...values.services];
                      updated[serviceIndex].value = e.target.value;
                      setValues({ ...values, services: updated });
                    }}
                    style={{ width: "160px" }}
                    error={Boolean(errors[`services[${serviceIndex}].value`])}
                    helperText={errors[`services[${serviceIndex}].value`] || ""}
                    InputProps={{
                      style: {
                        height: "45px",
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </div>
              </Grid>
            );
          })}

        <Grid item xs={12}>
          <Typography
            variant="h6"
            style={{
              fontWeight: 400,
              color: "rgba(244,151,3,.8)",
              margin: "0.1rem 0 0 0.1rem",
            }}
          >
            Reports
          </Typography>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid #e0e0e0",
              margin: "0 1rem 0 1rem",
            }}
          />
        </Grid>
        {values.services
          .filter((item) => checkboxServices.includes(item.name))
          .map((item) => {
            const serviceIndex = values.services.findIndex(
              (s) => s.name === item.name
            );
            return (
              <Grid item xs={12} sm={4} key={item.name}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0rem",
                    padding: "0.1rem 0.1rem",
                    borderRadius: "8px",
                    backgroundColor: "#f5f5f5",
                    margin: "0.1rem 1rem",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e0e0e0")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                >
                  <Typography
                    variant="body1"
                    style={{
                      flex: 1,
                      fontWeight: 500,
                      color: "#333",
                    }}
                  >
                    {item.name}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={item.value === true || item.value === "on"}
                        onChange={(e) => {
                          const updated = [...values.services];
                          updated[serviceIndex].value = e.target.checked;
                          setValues({ ...values, services: updated });
                        }}
                        name={`services[${serviceIndex}].value`}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#1976d2",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#1976d2",
                            },
                        }}
                      />
                    }
                    label=""
                    style={{ margin: 0 }}
                  />
                </div>
              </Grid>
            );
          })}

        <Grid item xs={12}>
          <div style={{ margin: "1rem" }}>
            <Controls.Button
              disabled={values.submitting}
              text={
                edite
                  ? values.submitting
                    ? "Editing..."
                    : "Done"
                  : values.submitting
                  ? "Adding..."
                  : "Add"
              }
              type="submit"
              style={{ marginRight: "1rem" }}
            />
            <Controls.Button
              text="Cancel"
              color="default"
              onClick={() => setOpenPopup(false)}
            />
          </div>
        </Grid>
      </Grid>
    </Form>
  );
};

export default AddSubscription;
